export const VIDEO_KEYFRAME_ACTION_TAGS = new Set(['h3', 'h3max', 'kling', 'kling4'])

const normalizedActionTag = (tag: unknown) =>
    String(tag || '')
        .trim()
        .replace(/^!+/, '')
        .toLowerCase()

// Frame-only models: always in keyframe mode, and an end frame needs a start frame.
const START_FRAME_REQUIRED_FOR_END_FRAME_TAGS = new Set(['h3max', 'kling', 'kling4'])

export const endFrameRequiresStartFrame = (tag: unknown) =>
    START_FRAME_REQUIRED_FOR_END_FRAME_TAGS.has(normalizedActionTag(tag))

export const supportsExplicitVideoKeyframes = (tag: unknown) =>
    VIDEO_KEYFRAME_ACTION_TAGS.has(normalizedActionTag(tag))

export const videoKeyframeModeForFormState = (
    tag: unknown,
    hasStartImage: boolean,
    frameMode = false,
) => {
    const normalizedTag = normalizedActionTag(tag)
    return (
        VIDEO_KEYFRAME_ACTION_TAGS.has(normalizedTag) &&
        (START_FRAME_REQUIRED_FOR_END_FRAME_TAGS.has(normalizedTag) || hasStartImage || frameMode)
    )
}

export const videoKeyframeInputs = (tag: unknown, inputs: Record<string, any> = {}) => {
    const normalizedTag = normalizedActionTag(tag)
    if (!VIDEO_KEYFRAME_ACTION_TAGS.has(normalizedTag)) {
        return { startImage: null, endImage: null, frameMode: false }
    }

    const references = inputs.references || {}
    const referenceImages = Array.isArray(references.images) ? references.images : []
    const explicitStartImage = inputs.start_image || inputs.startImage || null
    const explicitEndImage = inputs.end_image || inputs.endImage || null

    if (explicitStartImage || explicitEndImage) {
        return {
            startImage: explicitStartImage,
            endImage: explicitEndImage,
            frameMode: true,
        }
    }

    if (inputs.image) {
        return {
            startImage: inputs.image,
            endImage: referenceImages[0] || null,
            frameMode: true,
        }
    }

    if (START_FRAME_REQUIRED_FOR_END_FRAME_TAGS.has(normalizedTag) && referenceImages.length > 0) {
        return {
            startImage: referenceImages[0],
            endImage: referenceImages[1] || null,
            frameMode: true,
        }
    }

    return { startImage: null, endImage: null, frameMode: false }
}

export const videoKeyframeInputsFromFormState = (
    tag: unknown,
    image: Record<string, any> | null | undefined,
    referenceImages: Record<string, any>[] = [],
    frameMode = false,
) => {
    if (supportsExplicitVideoKeyframes(tag) && frameMode) {
        return {
            startImage: image || null,
            endImage: referenceImages[0] || null,
            frameMode: true,
        }
    }

    return videoKeyframeInputs(tag, {
        image,
        references: { images: referenceImages },
    })
}

export const explicitVideoKeyframePayload = (
    tag: unknown,
    startImage: Record<string, any> | null | undefined,
    endImage: Record<string, any> | null | undefined,
) => {
    if (!supportsExplicitVideoKeyframes(tag) || (!startImage && !endImage)) return null

    return {
        ...(startImage ? { start_image: startImage } : {}),
        ...(endImage ? { end_image: endImage } : {}),
    }
}
